var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class Xantech {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'STATUS',
                next: /Firmware Version|HDMI XT-HDMI-MX.*Status/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // Product-specific information for parsing the STATUS response.
        this.collectingStatusResponses = false;
        this.productInfo = [
            {
                model: 'MX42-4K',
                numberOutputs: 2,
                firmwareRegExp: /FW Version:\s+([\d+\.]*)/i,
                output: {
                    labelRegExp: /Output*\s+(?:FromIn|SelectInput)/i,
                    valueRegExp: /^(\d+)\s+(\S+)\s+(?:On|Off|Yes|No)\s+(?:On|Off|Yes|No)\s/i,
                },
                power: {
                    labelRegExp: /^Power\s+IR/i,
                    valueRegExp: /^(On|Off)\s+(?:On|Off)/i,
                },
            },
        ];
        this.product = this.productInfo[0];
        // Async handler for status responses, which are queried in onConfigure. Status responses start
        // and end with a "=========" line.
        this.statusAsyncHandler = {
            name: 'statusResponse',
            regExp: /^(?!(?:\s*)?\[SUCCESS\])([\s\S]*)/i,
            callback: (match) => {
                if (!this.collectingStatusResponses && match[1].startsWith('=====')) {
                    this.collectingStatusResponses = true;
                }
                else if (this.collectingStatusResponses && match[1].startsWith('=====')) {
                    this.collectingStatusResponses = false;
                    this.processStatus(this.statusResponseArray);
                }
                else if (this.collectingStatusResponses && match[1].length > 0) {
                    this.statusResponseArray.push(match[1]);
                }
            },
        };
        this.statusResponseArray = [];
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(power ? 'PON' : 'POFF', /\[SUCCESS\]Set system power (ON|OFF)|\[ERROR\]/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 10000 : 4000);
                this.framework.suspend(power ? 10000 : 2000);
                return match[1] ? match[1].toUpperCase() === 'ON' : this.state.power.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                if (key === '00')
                    key = '01'; // if querying all outputs, return value from 01
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`OUT${key}FR${value}`, /\[SUCCESS\]\s*Set\s+output\s+\d+\s+.+input\s+(\d+)|\[SUCCESS\]\s*Set\s+all output\s+.+input\s+(\d+)|\[(ERROR)\]/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 1000);
                if (match[1])
                    return match[1];
                if (match[2]) {
                    // Update all outputs,
                    for (let i = 1; i <= this.product.numberOutputs; i++) {
                        const newKey = i.toString().padStart(2, '0');
                        this.state.videoMatrixRoutes.update(newKey, value);
                    }
                    return match[2];
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                regExp: /\[SUCCESS\]\s*Set\s+output\s+(\d+)\s+.+input\s+(\d+)|\[(ERROR)\]/i,
                callback: (match) => {
                    const key = match[1];
                    const value = match[2];
                    this.state.videoMatrixRoutes.update(key, value);
                },
            },
            powerOffRestrictions: ['set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () { return yield this.queryStatus(true); }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.product.numberOutputs = Object.values((_a = config.videoOutputs) !== null && _a !== void 0 ? _a : {}).filter(v => {
                return v.code !== '00'; // ignore "all outputs" when determining length
            }).length;
            if (this.statusAsyncHandlerId === undefined) {
                this.statusAsyncHandlerId = this.socket.registerAsync(this.statusAsyncHandler);
            }
            yield this.queryStatus();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            if (this.statusAsyncHandlerId !== undefined) {
                this.socket.unregisterAsync(this.statusAsyncHandlerId);
                this.statusAsyncHandlerId = undefined;
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    processStatus(data) {
        let match;
        // Iterate through status response looking for a label match, then process the value from
        // the next line.
        let i = 0;
        while (i < data.length) {
            if ((match = this.product.firmwareRegExp.exec(data[i]))) {
                i++;
                this.state.firmwareVersion.update(match[1]);
            }
            else if ((match = this.product.power.labelRegExp.exec(data[i]))) {
                i++;
                if ((match = this.product.power.valueRegExp.exec(data[i]))) {
                    this.state.power.update(match[1].toUpperCase() === 'ON');
                    i++;
                }
            }
            else if ((match = this.product.output.labelRegExp.exec(data[i]))) {
                i++;
                while ((match = this.product.output.valueRegExp.exec(data[i]))) {
                    if (match) {
                        i++;
                        this.state.videoMatrixRoutes.update(match[1], match[2]);
                    }
                }
            }
            else {
                i++;
            }
        }
    }
    queryStatus(isPoll = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? ['poll'] : [];
            this.statusResponseArray = [];
            this.collectingStatusResponses = false;
            yield this.socket.send('STATUS', /STATUS$/, tags);
        });
    }
}
// static properties
Xantech.driverType = 'socketDriver';
Xantech.key = '-NE_kqNRqInHMgxB7lAx';
Xantech.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        port: 4999,
        sendTerminator: '\r',
    },
];
